/**
 * CapacityAuditTab — audit de la chaîne capacité, pilotée CONTRAT.
 * Détail par personne + synthèse par pool. Ligne unique :
 *   EPT (Σ contrats) · OT (=TT calendrier −congés, «TO normal/EPT»)
 *   · [Corp · Super · C.spéc = occupation effective] · TR · EPT capacité (=TR÷OT 100%)
 *   · [TF · NF · ABS = RÉEL mensuel] · Idle (=TR−TF−NF−ABS).
 * EPT affichés en % par ligne, en EPT (1 déc.) sur la ligne Total.
 * Charte Palestre uniquement, tables triables par colonne.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCapacityAudit } from '../../api/capacityOverview';
import Card from '../../components/Card';
import { tp, useP } from '../../i18n/useP';
import './CapacityAuditTab.css';

const fmtH = (v) => (v == null ? '—' : String(Math.round(v)).replace(/\B(?=(\d{3})+(?!\d))/g, '’'));
const fmtPct = (v) => (v == null ? '—' : Math.round(v * 100) + ' %');     // EPT par ligne
const fmtE1 = (v) => (v == null ? '—' : (Math.round(v * 10) / 10).toFixed(1)); // EPT total (1 déc.)

// Colonnes : base · structure standard · résultat capacité · usage réel.
// `pct` : affiché en % par ligne, en EPT (1 déc.) sur la ligne Total.
// `hours` : colonne d'heures, basculable en % de l'OT via le toggle.
const COLS = [
  { k: 'taux', label: 'Taux', pct: true },
  { k: 'ept', label: 'EPT', pct: true, phare: true },
  { k: 'ot', label: 'OT', fmt: fmtH, hours: true },
  { k: 'corp', label: 'Corp', fmt: fmtH, grp: 'std', hours: true },
  { k: 'supervision', label: 'Superv.', fmt: fmtH, grp: 'std', hours: true },
  { k: 'special', label: tp('phrases:p_0d4d69c983'), fmt: fmtH, grp: 'std', hours: true },
  { k: 'tr', label: 'TR', fmt: fmtH, grp: 'res', hours: true },
  { k: 'ept_cap', label: 'EPT cap.', pct: true, phare: true, grp: 'res' },
  { k: 'tf', label: 'TF', fmt: fmtH, grp: 'real', hours: true },
  { k: 'nf', label: 'NF', fmt: fmtH, grp: 'real', hours: true },
  { k: 'abs', label: 'ABS', fmt: fmtH, grp: 'real', hours: true },
  { k: 'idle', label: 'Idle', fmt: fmtH, grp: 'real', hours: true },
];

const SUM_KEYS = ['taux', 'ept', 'ot', 'corp', 'supervision', 'special', 'tr', 'tf', 'nf', 'abs', 'idle'];

// Largeurs fixes partagées → les colonnes des 2 tables s'alignent verticalement.
// La table pool a 1 colonne de libellé (= NAME_W + POOL_W) ; la table personne en a 2.
const NAME_W = 148;
const POOL_W = 92;
const COL_W = 72;

function AuditTable({ rows, totals, labelHead, showPool, pctMode, unit }) {
  const [sort, setSort] = useState(null); // {k, dir}
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const dir = sort.dir === 'asc' ? 1 : -1;
    const acc = sort.k === 'label' ? (r) => r.label
      : sort.k === 'pool' ? (r) => r.pool || ''
        : (r) => r[sort.k];
    return [...rows].sort((a, b) => {
      const va = acc(a), vb = acc(b);
      if (typeof va === 'string') return va.localeCompare(vb) * dir;
      return ((va || 0) - (vb || 0)) * dir;
    });
  }, [rows, sort]);

  const onSort = (k) => setSort((s) =>
    s && s.k === k ? (s.dir === 'desc' ? { k, dir: 'asc' } : null) : { k, dir: 'desc' });
  const arrow = (k) => (sort && sort.k === k ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : '');

  // Colonne de base du mode % : OT en % OT, TR en % TR → affichée à 100 % et surlignée.
  const baseKey = unit === 'ot' ? 'ot' : unit === 'tr' ? 'tr' : null;
  const cls = (c) => `num${c.phare ? ' phare' : ''}${c.hl ? ' hl' : ''}${c.k === baseKey ? ' base' : ''}${c.grp ? ' g-' + c.grp : ''}`;
  // EPT/Taux : % par ligne individuelle (pctMode), décimal pool+total.
  // Heures : en h, ou en % de l'OT / du TR de la ligne selon le toggle.
  const fmtCell = (c, v, isTotal, ot, tr) => {
    if (c.pct) return (isTotal || !pctMode) ? fmtE1(v) : fmtPct(v);
    if (c.hours && unit !== 'h') {
      const base = unit === 'ot' ? ot : tr;
      return base ? Math.round((v / base) * 100) + ' %' : '—';
    }
    return c.fmt(v);
  };
  const cellRow = (r, isTotal) => COLS.map((c) => <td key={c.k} className={cls(c)}>{fmtCell(c, r[c.k], isTotal, r.ot, r.tr)}</td>);

  return (
    <table className="cau-table" style={{ minWidth: NAME_W + POOL_W + COL_W * COLS.length }}>
      <colgroup>
        <col style={{ width: showPool ? NAME_W : NAME_W + POOL_W }} />
        {showPool && <col style={{ width: POOL_W }} />}
        {COLS.map((c) => <col key={c.k} style={{ width: COL_W }} />)}
      </colgroup>
      <thead>
        <tr className="cau-grp">
          <th className="lbl">&nbsp;</th>
          {showPool && <th>&nbsp;</th>}
          <th colSpan={3}>&nbsp;</th>
          <th className="g-std" colSpan={3}>Structure (occupation)</th>
          <th className="g-res" colSpan={2}>Capacité prévue</th>
          <th className="g-real" colSpan={4}>Usage capacité (réel)</th>
        </tr>
        <tr>
          <th className="lbl sortable" onClick={() => onSort('label')}>{labelHead}{arrow('label')}</th>
          {showPool && <th className="lbl sortable" onClick={() => onSort('pool')}>Pool{arrow('pool')}</th>}
          {COLS.map((c) => (
            <th key={c.k} className={`${cls(c)} sortable`} onClick={() => onSort(c.k)}>{c.label}{arrow(c.k)}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => (
          <tr key={r.label}>
            <td className="lbl">{r.label}</td>
            {showPool && <td className="lbl cau-sub">{r.pool || '—'}</td>}
            {cellRow(r)}
          </tr>
        ))}
      </tbody>
      {totals && (
        <tfoot>
          <tr className="cau-tot">
            <td className="lbl">Total</td>
            {showPool && <td className="lbl">&nbsp;</td>}
            {cellRow(totals, true)}
          </tr>
        </tfoot>
      )}
    </table>
  );
}

const TYPES = [
  { k: 'direct', label: 'Direct' },
  { k: 'overhead', label: 'Overhead' },
  { k: 'direction', label: 'Direction' },
];

export default function CapacityAuditTab({ tenantId, year, addToast }) {
  const { t } = useTranslation();
  const p = useP();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selPools, setSelPools] = useState(() => new Set());
  const [selTypes, setSelTypes] = useState(() => new Set());
  const [unit, setUnit] = useState('h'); // 'h' | 'ot' (% OT) | 'tr' (% TR)

  useEffect(() => {
    if (!tenantId) { setData(null); return; }
    setLoading(true);
    getCapacityAudit(tenantId, year)
      .then((r) => setData((r && r.data) || r || null))
      .catch((e) => addToast && addToast(e?.response?.data?.detail || p('Échec du chargement de l’audit capacité'), 'error'))
      .finally(() => setLoading(false));
  }, [tenantId, year]); // eslint-disable-line

  const poolOptions = useMemo(
    () => (data?.pools || []).map((p) => ({ name: p.pool, type: p.type }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [data]);

  if (loading) return <p className="page-table-loading">Chargement…</p>;
  if (!data) return null;

  const toggle = (setFn) => (key) => setFn((s) => {
    const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n;
  });
  // Filtre : ensembles vides = tout afficher ; sinon ET entre type et pool.
  const passes = (poolName, poolType) =>
    (selTypes.size === 0 || selTypes.has(poolType)) &&
    (selPools.size === 0 || selPools.has(poolName));

  const poolRows = (data.pools || [])
    .filter((p) => passes(p.pool, p.type)).map((p) => ({ ...p, label: p.pool }));
  const personRows = (data.persons || [])
    .filter((p) => passes(p.pool || '—', p.pool_type)).map((p) => ({ ...p, label: p.name }));

  const total = personRows.reduce((a, r) => {
    SUM_KEYS.forEach((k) => { a[k] += r[k] || 0; });
    return a;
  }, Object.fromEntries(SUM_KEYS.map((k) => [k, 0])));
  total.ept_cap = data.ot_100 ? total.tr / data.ot_100 : 0;
  total.label = 'Total';

  return (
    <div className="cau">
      <div className="cau-anchor">
        Période : <b>{data.closed_months} mois cumulés</b>
        <span className="cau-anchor-detail"> · OT pour 1 EPT à 100 % = {fmtH(data.ot_100)} h (annuel {fmtH(data.ot_100_annual)})</span>
        <span className="cau-anchor-detail"> · EPT capacité = TR ÷ {fmtH(data.ot_100)} · Idle = TR − TF − NF − ABS</span>
      </div>
      <Card title={t('phrases:p_ac0002b077')}>
        <div className="cau-chips">
          <span className="cau-chips-lbl l-unit">Unité</span>
          <span className="cau-toggle">
            <button type="button" className={unit === 'h' ? 'on' : ''} onClick={() => setUnit('h')}>h</button>
            <button type="button" className={unit === 'ot' ? 'on' : ''} onClick={() => setUnit('ot')}>% TO</button>
            <button type="button" className={unit === 'tr' ? 'on' : ''} onClick={() => setUnit('tr')}>% TR</button>
          </span>
          <span className="cau-chips-sep" />
          <span className="cau-chips-lbl l-type">Type</span>
          {TYPES.map((t) => (
            <button key={t.k} type="button" className={`cau-chip cau-chip--type${selTypes.has(t.k) ? ' on' : ''}`}
              onClick={() => toggle(setSelTypes)(t.k)}>{t.label}</button>
          ))}
          {(selPools.size > 0 || selTypes.size > 0) && (
            <button type="button" className="cau-chip cau-chip--clear"
              onClick={() => { setSelPools(new Set()); setSelTypes(new Set()); }}>✕ tout</button>
          )}
        </div>
        <div className="cau-chips">
          <span className="cau-chips-lbl l-pool">Pool</span>
          {poolOptions.map((p) => (
            <button key={p.name} type="button" className={`cau-chip cau-chip--pool${selPools.has(p.name) ? ' on' : ''}`}
              onClick={() => toggle(setSelPools)(p.name)}>{p.name}</button>
          ))}
        </div>
      </Card>
      <Card title={t('phrases:p_0e1fcfccf7')} flush>
        <div className="cau-table-wrap">
          <AuditTable rows={poolRows} totals={total} labelHead="Pool" unit={unit} />
        </div>
      </Card>
      <Card title={t('phrases:p_da35212827')} flush>
        <div className="cau-table-wrap">
          <AuditTable rows={personRows} totals={total} labelHead="Personne" showPool pctMode unit={unit} />
        </div>
      </Card>
    </div>
  );
}
